import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { WancomHttpService } from '../../../services/wancom-http/wancom-http.service';
import { AlertService } from '../../../services/alert/alert.service';
import { environment } from '../../../../environments/environment';
import { lastValueFrom } from 'rxjs';
import { colorPalette } from '../../account-detail/colorPalette.model';

@Injectable({
    providedIn: 'root',
})
export class SignInService {
    constructor(
        private http: WancomHttpService,
        private router: Router,
        private alertService: AlertService
    ) {}

    signIn(
        username: string,
        password: string,
        code: string,
        firebaseUID?: string,
        fromStore: boolean = true
    ) {
        const signInInput: SignInParams = {
            username: username,
            password: password,
            tfaCode: code,
            firebaseUID: firebaseUID,
        };
        return lastValueFrom(
            this.http.post<SignInResult>(
                'account/sign-in',
                signInInput,
                fromStore ? environment.apiBaseStore : environment.apiBaseUrl
            )
        ).then((result: SignInResult) => {
            if (result.token) {
                this.http.setToken(result.token);
            }

            if (result.mfa && result.error) {
                this.alertService.show({
                    message: 'Code de vérification incorrect.',
                    duration: 5000,
                });
            }

            return result;
        });
    }

    createAccount(
        firstName: string,
        lastName: string,
        email: string,
        password: string
    ) {
        return this.http
            .post<SignInResult>('account/create-account', {
                firstName,
                lastName,
                email,
                password,
            })
            .subscribe((result) => {
                this.http.setToken(result.token);
                window.location.pathname = '/';
            });
    }

    createStoreAccount(newUserInfos: CreateAccountParams) {
        return lastValueFrom(
            this.http.post<any>(
                'account/createAccount',
                newUserInfos,
                environment.apiBaseStore
            )
        );
    }

    forgotPassword(email: string) {
        return lastValueFrom(
            this.http.post(
                'account/forgot-password',
                { Email: email },
                environment.apiBaseStore
            )
        );
    }

    /**
     *
     * @param searchedUserName Login ou mail de l'utilisateur recherché
     * @returns 200 ok s'il existe / 404 Si inconnu
     */
    checkLoginMail(searchedUserName: string) {
        return this.http.post<IsExistOutput>(
            'account/IsExist',
            { login: searchedUserName },
            environment.apiBaseStore
        );
    }

    /**
     *
     * @param firebaseUID UID Firebase de l'utilisateur qui essaye de se connecter
     * @returns 200 ok s'il existe / 404 Si inconnu
     */
    checkFirebaseUID(firebaseUID: string) {
        return this.http.post<IsExistOutput>(
            'account/IsExistFireBase',
            { firebaseUID: firebaseUID },
            environment.apiBaseStore
        );
    }

    /**
     * Confirme le mail de l'utilisateur via son token envoyé par mail
     *
     * @param emailRecoveryToken Token de vérification du mail
     * @returns
     */
    validationMail(emailRecoveryToken: string, userMail: string) {
        return this.http.post(
            `account/validationMail`,
            { emailRecoveryToken: emailRecoveryToken, email: userMail },
            environment.apiBaseStore
        );
    }

    /**
     * Renvoi un mail de vérification de compte Wanview
     * @param inputMail Mail de l'utilisateur
     * @returns
     */
    newValidationMail(inputMail: string) {
        return this.http.post(
            `account/newValidationMail`,
            { Email: inputMail },
            environment.apiBaseStore
        );
    }
}

export class SignInParams {
    username: string;
    password: string;
    tfaCode: string;

    firebaseUID?: string;
}

export class SignInResult {
    token: string;
    mfa: boolean;
    mfaMode: string;
    error: boolean;
    success: boolean;
    message: string;
}

export interface IsExistOutput {
    canAccessWanview: boolean;
    isActive: boolean;
    emailConfirmed: boolean;
}

/**
 * Interface pour la création d'un nouvel utilisateur via le store
 */
export interface CreateAccountParams {
    /**
     * Mail du nouvel utilisateur => correspond à l'username
     */
    email: string;
    /**
     * Identifiant unique Firebase du nouvel utilisateur
     */
    firebaseUID?: string;
    /**
     * Prénom du nouvel utilisateur
     */
    firstName: string;
    /**
     * Nom du nouvel utilisateur
     */
    lastName: string;
    /**
     * Id de l'activité principale de l'orga du nouvel utilisateur
     */
    organizationActivityId: number;
    /**
     * Raison sociale de l'entreprise du nouvel utilisateur
     */
    organizationName: string;
    /**
     * N° de siret de l'entreprise du nouvel utilisateur
     */
    siretNumber: string;
    /**
     * Mdp du nouvel utilisateur
     */
    password?: string;
    /**
     * Numéro de téléphone du nouvel utilisateur
     */
    phoneNumber: string;
    /**
     * L'utilisateur à bien lu et validé les CGU
     */
    isOkCGU: boolean;
    /**
     * L'utilisateur à bien lu et validé les CGV
     */
    isOkCGV: boolean;
    address: string;
    zipCode: string;
    cityName: string;
    organizationTypeId: number;
    colorPalette?: colorPalette;
}
